import React from "react";
import { Modal, Button, Dropdown } from "antd";
import { SaveOutlined, ArrowRightOutlined, MoreOutlined, UndoOutlined } from "@ant-design/icons";
import styled from "styled-components";
import type { ChangeRow, ChangeType } from "../../utils/changeSummary";

const StyledModal = styled(Modal)`
	.ant-modal-content {
		background: #f3f5ff !important;
		border-radius: 24px !important;
		padding: 32px !important;
		box-shadow: 0px 12px 24px 0px #1559a53d !important;
	}
`;

const Header = styled.div`
	display: flex;
	flex-direction: column;
	gap: 8px;
	margin-bottom: 20px;
`;

const Title = styled.div`
	font-size: 24px;
	font-weight: 600;
	color: #1a1a1a;
`;

const Description = styled.div`
	font-size: 14px;
	color: #595959;
`;

const TableWrapper = styled.div`
	border-radius: 12px;
	overflow: hidden;
	background: #fff;
	max-height: 360px;
	overflow-y: auto;
`;

const Table = styled.table`
	width: 100%;
	border-collapse: collapse;
	font-size: 14px;
`;

const Thead = styled.thead`
	th {
		position: sticky;
		top: 0;
		z-index: 1;
		background: #e9ebff;
		color: #3a0ca3;
		font-weight: 600;
		text-align: left;
		padding: 12px 16px;
	}
`;

const Tbody = styled.tbody`
	td {
		padding: 14px 16px;
		border-top: 1px solid #f0f0f0;
		vertical-align: top;
	}
	tr:first-child td {
		border-top: none;
	}
`;

const TabCell = styled.td`
	font-weight: 600;
	color: #1a1a1a;
	white-space: nowrap;
	width: 120px;
`;

const TypeCell = styled.td`
	width: 96px;
`;

const TYPE_STYLES: Record<ChangeType, { bg: string; color: string }> = {
	Added: { bg: "#E6F4EA", color: "#1E7A34" },
	Edited: { bg: "#FFF3E0", color: "#C77700" },
	Deleted: { bg: "#FDECEC", color: "#D30024" },
};

const TypeBadge = styled.span<{ $type: ChangeType }>`
	display: inline-block;
	padding: 2px 12px;
	border-radius: 999px;
	font-size: 13px;
	font-weight: 600;
	background: ${({ $type }) => TYPE_STYLES[$type].bg};
	color: ${({ $type }) => TYPE_STYLES[$type].color};
`;

const DetailCell = styled.td`
	color: #1a1a1a;
`;

const ActionCell = styled.td`
	width: 40px;
	text-align: right;
`;

const KebabButton = styled(Button)`
	color: #595959;
	&:hover {
		color: #3a0ca3 !important;
		background: #ededff !important;
	}
`;

const DetailRow = styled.div`
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	gap: 8px;
`;

const FieldPill = styled.span`
	display: inline-block;
	padding: 2px 10px;
	border-radius: 6px;
	background: #f0f0f0;
	color: #595959;
	font-size: 13px;
`;

const FromText = styled.span`
	color: #8c8c8c;
`;

const ToText = styled.span`
	color: #1a1a1a;
	font-weight: 600;
`;

const DetailText = styled.span`
	color: #1a1a1a;
	font-weight: 600;
	white-space: pre-line;
`;

const EmptyState = styled.div`
	padding: 32px 16px;
	text-align: center;
	color: #8c8c8c;
`;

const Footer = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: center;
	margin-top: 24px;
`;

const RightButtons = styled.div`
	display: flex;
	gap: 12px;
`;

const OutlineButton = styled(Button)`
	height: 48px;
	min-width: 130px;
	border-radius: 24px;
	border: 2px solid #3a0ca3;
	color: #3a0ca3;
	font-weight: 600;

	&:hover,
	&:focus-visible {
		box-shadow: inset 0 0 0 2px #3a0ca3 !important;
		border-color: #3a0ca3 !important;
		color: #3a0ca3 !important;
	}

	&:disabled,
	&.ant-btn-disabled {
		border-color: #d9d9d9 !important;
		color: #8c8c8c !important;
		background: #f5f5f5 !important;
		cursor: not-allowed;
		box-shadow: none !important;
	}
`;

const ConfirmButton = styled(Button)`
	height: 48px;
	min-width: 150px;
	border-radius: 24px;
	background: #3a0ca3 !important;
	color: #fff !important;
	font-weight: 600;
	border: none;

	&:hover {
		background: #3a0ca3 !important;
		opacity: 0.9;
	}

	&:disabled,
	&.ant-btn-disabled {
		background: #d9d9d9 !important;
		border-color: #d9d9d9 !important;
		color: #8c8c8c !important;
		cursor: not-allowed;
		opacity: 1 !important;
	}
`;

interface ReviewChangesModalProps {
	open: boolean;
	changes: ChangeRow[];
	onBack: () => void;
	onUndo: (row: ChangeRow) => void;
	onDiscard: () => void;
	onConfirm: () => void;
	canSave?: boolean;
	saving?: boolean;
}

const renderDetail = (row: ChangeRow): React.ReactNode => {
	const hasBeforeAfter = row.from !== undefined && row.to !== undefined;
	return (
		<DetailRow>
			{row.label && <FieldPill>{row.label}</FieldPill>}
			{hasBeforeAfter ? (
				<>
					<FromText>{row.from}</FromText>
					<ArrowRightOutlined style={{ color: "#8c8c8c", fontSize: 12 }} />
					<ToText>{row.to}</ToText>
				</>
			) : (
				row.detail && <DetailText>{row.detail}</DetailText>
			)}
		</DetailRow>
	);
};

export const ReviewChangesModal: React.FC<ReviewChangesModalProps> = ({
	open,
	changes,
	onBack,
	onUndo,
	onDiscard,
	onConfirm,
	canSave = true,
	saving = false,
}) => {
	return (
		<StyledModal
			title={null}
			open={open}
			onCancel={saving ? undefined : onBack}
			footer={null}
			closable={false}
			maskClosable={!saving}
			keyboard={!saving}
			centered
			width={620}
		>
			<Header>
				<SaveOutlined style={{ fontSize: 24, color: "#3a0ca3" }} />
				<Title>Save Changes</Title>
				<Description>
					Please review all modifications made to the campaign details below before confirming your changes.
				</Description>
			</Header>

			<TableWrapper>
				{changes.length === 0 ? (
					<EmptyState>No changes to review.</EmptyState>
				) : (
					<Table>
						<Thead>
							<tr>
								<th>Tab</th>
								<th>Type</th>
								<th>Change Details</th>
								<th aria-label="Actions" />
							</tr>
						</Thead>
						<Tbody>
							{changes.map((row) => (
								<tr key={row.id}>
									<TabCell>{row.tab}</TabCell>
									<TypeCell>
										<TypeBadge $type={row.type}>{row.type}</TypeBadge>
									</TypeCell>
									<DetailCell>{renderDetail(row)}</DetailCell>
									<ActionCell>
										<Dropdown
											trigger={["click"]}
											disabled={saving}
											menu={{
												items: [
													{
														key: "undo",
														icon: <UndoOutlined />,
														label: "Undo this change",
													},
												],
												onClick: ({ key, domEvent }) => {
													domEvent.stopPropagation();
													if (key === "undo") onUndo(row);
												},
											}}
										>
											<KebabButton type="text" icon={<MoreOutlined />} disabled={saving} />
										</Dropdown>
									</ActionCell>
								</tr>
							))}
						</Tbody>
					</Table>
				)}
			</TableWrapper>

			<Footer>
				<OutlineButton onClick={onDiscard} disabled={saving}>
					Discard Changes
				</OutlineButton>
				<RightButtons>
					<OutlineButton onClick={onBack} disabled={saving}>
						Back
					</OutlineButton>
					<ConfirmButton
						type="primary"
						onClick={onConfirm}
						disabled={!canSave || changes.length === 0 || saving}
						loading={saving}
					>
						Confirm &amp; Save
					</ConfirmButton>
				</RightButtons>
			</Footer>
		</StyledModal>
	);
};
